import {
  AgGridText,
  Flex,
  Popover,
  useCurrenciesContext,
  usePopoverState,
  type AgGridTextParams,
  type Security,
} from '@talos/kyoko';
import type { ICellRendererParams } from 'ag-grid-enterprise';
import type { ReactNode } from 'react';
import { getProductTemplateBlotterTooltipValue } from './getProductTemplateBlotterTooltipValue';
import { hasProductTemplateTooltipSections } from './getProductTemplateTooltipModel';
import { ProductTemplateTooltip } from './ProductTemplateTooltip';

const PRODUCT_TEMPLATE_BLOTTER_POPOVER_DELAY_MS = 500;

export function ProductTemplateBlotterCell(params: ICellRendererParams & AgGridTextParams) {
  const security = getProductTemplateBlotterTooltipValue(params);
  const showProductTemplateTooltip = security != null && hasProductTemplateTooltipSections(security);
  const cell = (
    <Flex w="100%" h="100%" minWidth="0" alignItems="center" data-testid="product-template-blotter-cell">
      <AgGridText
        {...params}
        columnParams={{
          ...params.columnParams,
          // Only suppress the ellipsis tooltip when the rich product-template popover is mounted.
          suppressOverflowTooltip: showProductTemplateTooltip,
        }}
      />
    </Flex>
  );

  if (!showProductTemplateTooltip || security == null) {
    return cell;
  }

  return <ProductTemplateBlotterCellPopover security={security}>{cell}</ProductTemplateBlotterCellPopover>;
}

function ProductTemplateBlotterCellPopover({ children, security }: { children: ReactNode; security: Security }) {
  const popover = usePopoverState({
    trigger: 'hover',
    usePortal: true,
    // Overlay the next column; flip to the previous column when the viewport has no room on the right.
    placement: 'right',
    delay: PRODUCT_TEMPLATE_BLOTTER_POPOVER_DELAY_MS,
    closeOnClickOutside: true,
    // AG Grid can remount the cell renderer while the pointer is still over it.
    resumeHoverOnTargetMount: true,
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['left'],
        },
      },
      {
        name: 'preventOverflow',
        enabled: true,
      },
    ],
  });

  return (
    <Popover {...popover} tabIndex={-1} targetStyle={{ width: '100%', height: '100%' }}>
      {children}
      <ProductTemplateBlotterCellTooltip security={security} />
    </Popover>
  );
}

function ProductTemplateBlotterCellTooltip({ security }: { security: Security }) {
  const { currenciesBySymbol } = useCurrenciesContext();

  return <ProductTemplateTooltip security={security} currenciesBySymbol={currenciesBySymbol} />;
}
